import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  Plus,
  FileText,
  User,
  Calendar,
  CheckCircle,
  ExternalLink,
  Image,
  Video,
  File,
  Clock,
  Upload,
  Loader2,
} from 'lucide-react';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/Button';
import { PageTransition } from '../../components/animations/PageTransition';
import { HtmlRenderer, RichTextEditor } from '../../components/editor';
import { BUNDLE_STATUSES, BUNDLE_STATUS_FLOW, WORK_ITEM_STATUSES } from '@yoda/shared';
import type { BundleStatus } from '@yoda/shared';
import { useUIStore } from '../../stores/uiStore';

interface WorkItemSummary {
  id: string;
  title: string;
  type: string;
  status: string;
  fileUrl: string | null;
  thumbnailUrl: string | null;
  createdAt: string;
}

interface BundleDetail {
  id: string;
  projectId: string;
  requestId: string | null;
  title: string;
  descriptionHtml: string | null;
  status: BundleStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  project: { id: string; name: string };
  request: { id: string; title: string } | null;
  creator: { id: string; name: string; avatarUrl: string | null };
  approver: { id: string; name: string; avatarUrl: string | null } | null;
  workItems: WorkItemSummary[];
  workItemCount: number;
  approvedItemCount: number;
}

const typeIcons: Record<string, typeof Image> = {
  image: Image,
  video: Video,
  document: FileText,
  other: File,
};

export function BundleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setSidebarCollapsed } = useUIStore();

  const [bundle, setBundle] = useState<BundleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', descriptionHtml: '' });
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkTitles, setBulkTitles] = useState('');
  const [addingItems, setAddingItems] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedWorkItemId, setSelectedWorkItemId] = useState<string | null>(null);

  // Collapse sidebar when editing
  useEffect(() => {
    if (isEditing) {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
    return () => setSidebarCollapsed(false);
  }, [isEditing, setSidebarCollapsed]);

  useEffect(() => {
    if (id) {
      fetchBundle();
    }
  }, [id]);

  const fetchBundle = async () => {
    setLoading(true);
    try {
      const response = await api.get<BundleDetail>(`/bundles/${id}`);
      const data = response.data;
      if (data) {
        setBundle(data);
        setEditForm({
          title: data.title,
          descriptionHtml: data.descriptionHtml || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch bundle:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!bundle) return;
    setSaving(true);
    try {
      await api.patch(`/bundles/${bundle.id}`, {
        title: editForm.title,
        descriptionHtml: editForm.descriptionHtml,
      });
      setBundle({ ...bundle, ...editForm });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!bundle) return;
    try {
      await api.patch(`/bundles/${bundle.id}/status`, { status: newStatus });
      setBundle({ ...bundle, status: newStatus as BundleStatus });
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  const handleBulkAddItems = async () => {
    if (!bundle || !bulkTitles.trim()) return;

    const titles = bulkTitles
      .split('\n')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    if (titles.length === 0) return;

    setAddingItems(true);
    try {
      await api.post(`/bundles/${bundle.id}/work-items/bulk`, { titles });
      setBulkTitles('');
      setShowAddModal(false);
      fetchBundle();
    } catch (error) {
      console.error('Failed to add items:', error);
    } finally {
      setAddingItems(false);
    }
  };

  const handleRemoveItem = async (workItemId: string) => {
    if (!bundle || !window.confirm('Remove this work item from the bundle?')) return;
    try {
      await api.delete(`/bundles/${bundle.id}/work-items/${workItemId}`);
      setBundle({
        ...bundle,
        workItems: bundle.workItems.filter((w) => w.id !== workItemId),
        workItemCount: bundle.workItemCount - 1,
      });
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const handleUploadClick = (workItemId: string) => {
    setSelectedWorkItemId(workItemId);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedWorkItemId) return;

    setUploadingItemId(selectedWorkItemId);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.upload(`/uploads/work-item-version/${selectedWorkItemId}`, formData);
      toast.success('File uploaded successfully!');
      fetchBundle(); // Refresh to get updated work item
    } catch (error: any) {
      console.error('Failed to upload:', error);
      toast.error(error.message || 'Failed to upload file');
    } finally {
      setUploadingItemId(null);
      setSelectedWorkItemId(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!bundle || !window.confirm('Delete this bundle?')) return;
    try {
      await api.delete(`/bundles/${bundle.id}`);
      navigate('/bundles');
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="space-y-6">
          <div className="h-8 w-48 bg-bg-secondary rounded animate-pulse" />
          <div className="h-64 bg-bg-secondary rounded-2xl animate-pulse" />
        </div>
      </PageTransition>
    );
  }

  if (!bundle) {
    return (
      <PageTransition>
        <div className="text-center py-12">
          <p className="text-text-secondary">Bundle not found</p>
          <Link to="/bundles" className="text-accent hover:underline mt-2 inline-block">
            Back to Bundles
          </Link>
        </div>
      </PageTransition>
    );
  }

  const statusInfo = BUNDLE_STATUSES[bundle.status];
  const allowedTransitions = BUNDLE_STATUS_FLOW[bundle.status] || [];
  const progress = bundle.workItemCount > 0
    ? Math.round((bundle.approvedItemCount / bundle.workItemCount) * 100)
    : 0;

  // Edit mode
  if (isEditing) {
    return (
      <PageTransition>
        <div className="flex-1 flex flex-col min-h-0">
          {/* Header Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsEditing(false)}
                className="p-2 rounded-xl hover:bg-bg-secondary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold">Edit Bundle</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} isLoading={saving}>
                Save
              </Button>
            </div>
          </div>

          {/* Form */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-6 gap-6 min-h-0">
            {/* Left Column - Title + Editor */}
            <div className="lg:col-span-5 flex flex-col min-h-0 order-2 lg:order-1">
              <div className="mb-3">
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Bundle title..."
                  className="w-full px-4 py-3 bg-bg-secondary border border-border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-accent/50"
                />
              </div>
              <div className="flex-1 min-h-0">
                <RichTextEditor
                  content={editForm.descriptionHtml}
                  onChange={(html) => setEditForm({ ...editForm, descriptionHtml: html })}
                  placeholder="Describe the bundle deliverables..."
                  className="h-full"
                />
              </div>
            </div>

            {/* Right Column - Info */}
            <div className="lg:col-span-1 space-y-4 order-1 lg:order-2">
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <span
                  className="inline-block px-3 py-1.5 rounded-lg text-sm font-medium"
                  style={{
                    backgroundColor: statusInfo?.bgColor,
                    color: statusInfo?.color,
                  }}
                >
                  {statusInfo?.label || bundle.status}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Project</label>
                <p className="text-sm text-text-secondary">{bundle.project.name}</p>
              </div>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Normal view mode
  return (
    <PageTransition>
      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,video/*,.pdf,.doc,.docx"
        onChange={handleFileSelect}
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-bg-secondary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-accent/10">
              <Package className="w-6 h-6 text-accent" />
            </div>

            <div>
              <h1 className="text-2xl font-bold">{bundle.title}</h1>
              <div className="flex items-center gap-2 text-text-secondary">
                <Link to={`/projects/${bundle.projectId}`} className="hover:text-accent">
                  {bundle.project.name}
                </Link>
                <span>-</span>
                <span>{bundle.workItemCount} items</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setIsEditing(true)}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Progress</h2>
                <span className="text-sm text-text-secondary">
                  {bundle.approvedItemCount} / {bundle.workItemCount} approved
                </span>
              </div>
              <div className="h-3 bg-bg-tertiary rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Description */}
            {bundle.descriptionHtml && (
              <div className="bg-bg-secondary rounded-2xl p-6">
                <h2 className="text-lg font-semibold mb-4">Description</h2>
                <HtmlRenderer html={bundle.descriptionHtml} />
              </div>
            )}

            {/* Work Items */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Work Items</h2>
                <Button size="sm" onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4" />
                  Add Items
                </Button>
              </div>

              {bundle.workItems.length === 0 ? (
                <div className="text-center py-8 text-text-muted">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No work items yet</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => setShowAddModal(true)}
                  >
                    <Plus className="w-4 h-4" />
                    Add Items
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {bundle.workItems.map((item) => {
                    const TypeIcon = typeIcons[item.type] || File;
                    const itemStatus = WORK_ITEM_STATUSES[item.status as keyof typeof WORK_ITEM_STATUSES];

                    return (
                      <div
                        key={item.id}
                        className="flex items-center gap-4 p-3 rounded-xl hover:bg-bg-tertiary transition-colors group"
                      >
                        {/* Thumbnail or Icon */}
                        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-bg-tertiary flex items-center justify-center">
                          {item.thumbnailUrl ? (
                            <img
                              src={item.thumbnailUrl}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <TypeIcon className="w-5 h-5 text-text-muted" />
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/work-items/${item.id}`}
                            className="font-medium hover:text-accent truncate block"
                          >
                            {item.title}
                          </Link>
                          <div className="flex items-center gap-2 text-sm text-text-secondary">
                            <span>{item.type}</span>
                            {!item.fileUrl && (
                              <>
                                <span>-</span>
                                <span className="text-amber-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Awaiting upload
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Status */}
                        <span
                          className="px-2 py-1 text-xs font-medium rounded-lg flex-shrink-0"
                          style={{
                            backgroundColor: itemStatus?.bgColor,
                            color: itemStatus?.color,
                          }}
                        >
                          {itemStatus?.label || item.status}
                        </span>

                        {/* Actions */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleUploadClick(item.id)}
                            disabled={uploadingItemId === item.id}
                            className="p-2 rounded-lg hover:bg-bg-secondary text-accent"
                            title={item.fileUrl ? 'Upload new version' : 'Upload file'}
                          >
                            {uploadingItemId === item.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                          </button>
                          <Link
                            to={`/work-items/${item.id}`}
                            className="p-2 rounded-lg hover:bg-bg-secondary"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="p-2 rounded-lg hover:bg-bg-secondary text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Source Request */}
            {bundle.request && (
              <div className="bg-violet-500/10 border border-violet-500/20 rounded-2xl p-6">
                <h2 className="text-sm font-medium text-violet-400 mb-2">Source Request</h2>
                <Link
                  to={`/requests/${bundle.request.id}`}
                  className="text-accent hover:underline font-medium"
                >
                  {bundle.request.title}
                </Link>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <h3 className="text-sm font-medium text-text-muted mb-3">Status</h3>
              <div className="space-y-3">
                <span
                  className="inline-block px-4 py-2 rounded-lg font-medium"
                  style={{
                    backgroundColor: statusInfo?.bgColor,
                    color: statusInfo?.color,
                  }}
                >
                  {statusInfo?.label || bundle.status}
                </span>

                {allowedTransitions.length > 0 && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-xs text-text-muted mb-2">Change to:</p>
                    <div className="flex flex-wrap gap-2">
                      {allowedTransitions.map((status) => {
                        const info = BUNDLE_STATUSES[status];
                        return (
                          <button
                            key={status}
                            onClick={() => handleStatusChange(status)}
                            className="px-3 py-1.5 text-xs rounded-lg border border-border hover:bg-bg-tertiary transition-colors"
                          >
                            {info?.label || status}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* People */}
            <div className="bg-bg-secondary rounded-2xl p-6 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-text-muted mb-2">Created by</h3>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                    {bundle.creator.avatarUrl ? (
                      <img
                        src={bundle.creator.avatarUrl}
                        alt=""
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <User className="w-4 h-4 text-accent" />
                    )}
                  </div>
                  <span className="font-medium">{bundle.creator.name}</span>
                </div>
              </div>

              {bundle.approver && (
                <div>
                  <h3 className="text-sm font-medium text-text-muted mb-2">Approved by</h3>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                      {bundle.approver.avatarUrl ? (
                        <img
                          src={bundle.approver.avatarUrl}
                          alt=""
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <User className="w-4 h-4 text-accent" />
                      )}
                    </div>
                    <span className="font-medium">{bundle.approver.name}</span>
                  </div>
                  {bundle.approvedAt && (
                    <p className="text-xs text-text-muted mt-1">
                      {new Date(bundle.approvedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <div className="flex items-center gap-2 text-text-muted text-sm">
                <Calendar className="w-4 h-4" />
                <span>Created {new Date(bundle.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Items Modal */}
      {showAddModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowAddModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-bg-secondary rounded-2xl p-6 w-full max-w-lg mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold mb-4">Add Work Items</h2>
            <p className="text-sm text-text-secondary mb-4">
              Enter titles for new placeholder work items (one per line). You can upload files to them later.
            </p>
            <textarea
              value={bulkTitles}
              onChange={(e) => setBulkTitles(e.target.value)}
              placeholder="Homepage banner&#10;Product page hero&#10;About us section"
              className="w-full h-40 px-4 py-3 bg-bg rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-accent/50 resize-none"
            />
            <div className="mt-4 flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleBulkAddItems}
                isLoading={addingItems}
                disabled={!bulkTitles.trim()}
              >
                Add Items
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </PageTransition>
  );
}
